import MeshWriter, { Attr, AttrMap, TypedArray } from './mesh-writer';

/**
 * Generate an axis-aligned XY plane with its lower-left vertex at (x, y), and
 * its upper right vertex at (x+width, y+height).
 */

export class PlaneMesh extends MeshWriter {
    x: number;
    y: number;
    width: number;
    height: number;

    constructor(x: number, y: number, width: number, height: number) {
        super();
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }

    get attrs() {
        let result: Set<Attr> = new Set();
        // name, size, count, type
        result.add(new Attr('position', 3, 6, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            x = this.x,
            y = this.y,
            w = this.width,
            h = this.height,
            ax = x,
            ay = y,
            bx = x + w,
            by = y,
            cx = x + w,
            cy = y + h,
            dx = x,
            dy = y + h,
            [_, pos] = attrMap.byName('position');
        PlaneMesh.write3(
            pos, 0,
            ax, ay, 0,
            bx, by, 0,
            cx, cy, 0,
            dx, dy, 0);
    }

    /**
     * A convenience method to write an arbitrary quad in 3D space. This is
     * actually more general than the PlaneMesh, which is always in the XY plane
     * with a zero Z coordinate. The triangles are written counter-clocwise,
     * assuming that the vertexes are also given counterclockwise. Thus the
     * two sub-triangles are ABC and ACD. This can write position, normal, or
     * vertex color data. The equivalent for UVs is write2().
     *
     *     D +------ + C
     *       |      /|
     *       |    /  |
     *       |  /    |
     *       |/      |
     *     A +------ + B
     *
     * @param d - The array to fill with data
     * @param i - the index at which to begin writing
     * @returns - the next index one might want to write into; this
     * helps if you want to do a bunch of quads and need help keeping track. It
     * will always be i+18 though, if you don't want to use it.
     */
    static write3(d: TypedArray, i: number,
        ax: number, ay: number, az: number,
        bx: number, by: number, bz: number,
        cx: number, cy: number, cz: number,
        dx: number, dy: number, dz: number) {
        // write the triangle ABC
        d[i++] = ax; d[i++] = ay; d[i++] = az;
        d[i++] = bx; d[i++] = by; d[i++] = bz;
        d[i++] = cx; d[i++] = cy; d[i++] = cz;

        // Now the triangle ACD
        d[i++] = ax; d[i++] = ay; d[i++] = az;
        d[i++] = cx; d[i++] = cy; d[i++] = cz;
        d[i++] = dx; d[i++] = dy; d[i++] = dz;

        return i;
    }

    /**
     * This is equivalent to write3, but writes two coordinates per vertex,
     * appropriate for UV coordinates. See the docs for write3.
     */
    static write2(d: TypedArray, i: number,
        au: number, av: number,
        bu: number, bv: number,
        cu: number, cv: number,
        du: number, dv: number) {
        // Triangle ABC
        d[i++] = au; d[i++] = av;
        d[i++] = bu; d[i++] = bv;
        d[i++] = cu; d[i++] = cv;

        // Triangle ACD
        d[i++] = au; d[i++] = av;
        d[i++] = cu; d[i++] = cv;
        d[i++] = du; d[i++] = dv;

        return i;
    }
}
